import { useCallback, useMemo, useState } from 'react';
import { MdClose, MdFileDownload, MdOutlineFileDownload } from 'react-icons/md';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { IconButton } from '@Components/IconButton';
import { downloadPNG } from '@src/utils/download-utils';
import { EMPTY_DATE_RANGE } from '../alexa-visibility-constants';
import { getTargetCitationSummary } from '../alexa-visibility-citation-utils';
import {
  getCompetitiveComparisons,
  getComparisonVerdict,
  getDrivingQueries,
  getFramingSummary,
  getMatrixBrands,
  getMostComparedAgainst,
  getVisibilityMatrix
} from '../alexa-visibility-competitive-utils';
import { filterResultsByDateRange } from '../alexa-visibility-utils';
import { useBrandColor } from '../hooks/useBrandColor';
import { usePrintView } from '../hooks/usePrintView';
import { CompetitiveMatrix } from './competitive-comparison/CompetitiveMatrix';
import {
  CompetitiveCitations,
  DrivingQueries,
  VerdictBanner
} from './competitive-comparison/CompetitiveInsights';
import { CompetitiveSummary } from './competitive-comparison/CompetitiveSummary';
import { QueryMultiSelect } from './QueryMultiSelect';

interface CompetitiveComparisonTabProps {
  // The page's results, already narrowed by the page-level date filter.
  results: AlexaVisibilityResult[];
  // Every result, before any date filter. The tab's own date filter starts from these, so it
  // overrides the page-level one.
  allResults: AlexaVisibilityResult[];
  targetBrand: string;
}

const TAB_CLASS = 'competitive-comparison-tab';
// The matrix and the stat cards are wide, so the print dialog is landscape. Added only while the
// print view is open so other pages' printing is unaffected.
const PRINT_PAGE_STYLE = '@page { size: landscape; margin: 0.5in; }';

export const CompetitiveComparisonTab = ({
  results,
  allResults,
  targetBrand
}: CompetitiveComparisonTabProps) => {
  // Empty means every query.
  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [isPrinting, setIsPrinting] = useState(false);

  const hasTabDateRange = Boolean(dateRange.start || dateRange.end);
  // The tab's own date filter replaces the page's when it is set.
  const dateScoped = useMemo(
    () =>
      hasTabDateRange
        ? filterResultsByDateRange(allResults, dateRange.start, dateRange.end)
        : results,
    [allResults, dateRange, hasTabDateRange, results]
  );

  const queryOptions = useMemo(
    () =>
      Array.from(new Set(dateScoped.map(result => result.query))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [dateScoped]
  );
  const activeQueries = useMemo(
    () => selectedQueries.filter(query => queryOptions.includes(query)),
    [queryOptions, selectedQueries]
  );
  const scoped = useMemo(
    () =>
      activeQueries.length === 0
        ? dateScoped
        : dateScoped.filter(result => activeQueries.includes(result.query)),
    [activeQueries, dateScoped]
  );

  const getBrandColor = useBrandColor(allResults, targetBrand);
  const brands = useMemo(() => getMatrixBrands(scoped, targetBrand), [scoped, targetBrand]);
  const matrixRows = useMemo(() => getVisibilityMatrix(scoped, brands), [brands, scoped]);

  const comparisons = useMemo(() => getCompetitiveComparisons(scoped), [scoped]);
  const framing = useMemo(() => getFramingSummary(comparisons), [comparisons]);
  const rank = useMemo(() => getTargetCitationSummary(comparisons), [comparisons]);
  const comparedAgainst = useMemo(
    () => getMostComparedAgainst(comparisons, targetBrand),
    [comparisons, targetBrand]
  );
  const drivingQueries = useMemo(() => getDrivingQueries(comparisons), [comparisons]);
  const newestFirst = useMemo(
    () =>
      [...comparisons].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [comparisons]
  );

  const closePrint = useCallback(() => setIsPrinting(false), []);
  usePrintView(isPrinting, closePrint, PRINT_PAGE_STYLE);

  const handleDownloadPng = (selector: string, name: string) => {
    downloadPNG(
      selector,
      `AlexaVisibility_${name}_${targetBrand}`,
      'competitive',
      ['no-print'],
      false
    );
  };

  if (allResults.length === 0) {
    return (
      <EmptyState
        title='No results yet'
        subtitle='The query x brand comparison will appear here once results are in.'
      />
    );
  }

  return (
    <div className={TAB_CLASS}>
      <div className='aars-tab-actions'>
        <div className='aars-tab-header'>
          <div className='aars-tab-title'>Competitive comparison</div>
          <div className='aars-tab-description'>
            How Alexa for Shopping positions {targetBrand} against competing brands — analyzing
            framing, query context, and competitive win/loss patterns across all runs.
          </div>
        </div>
        <div className='aars-export-buttons no-print'>
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdOutlineFileDownload />}
            onClick={() => setIsPrinting(true)}
          >
            PDF
          </Button>
          <Button
            size={Size.SM}
            design={Design.NEUTRAL}
            leadingElement={<MdFileDownload />}
            onClick={() => handleDownloadPng(`.${TAB_CLASS}`, 'CompetitiveComparison')}
          >
            PNG
          </Button>
        </div>
      </div>

      <div className='aars-cc-filters no-print'>
        <QueryMultiSelect
          options={queryOptions}
          selected={activeQueries}
          onChange={setSelectedQueries}
        />
        <div className='aars-date-filter'>
          <DatePicker
            range={hasTabDateRange ? dateRange : undefined}
            placeholder='All dates'
            onChange={({ start, end }) => setDateRange({ start, end })}
          />
          {hasTabDateRange && (
            <IconButton
              appearance={Appearance.EMPTY}
              aria-label='Clear date filter'
              onClick={() => setDateRange(EMPTY_DATE_RANGE)}
            >
              <MdClose />
            </IconButton>
          )}
        </div>
        <span className='aars-cc-filter-count'>
          {matrixRows.length} of {queryOptions.length} queries
        </span>
      </div>

      {matrixRows.length === 0 ? (
        <EmptyState
          title='No results match these filters'
          subtitle='Choose different queries or dates to see the comparison.'
        />
      ) : (
        <div className='aars-comp-sections'>
          <CompetitiveMatrix
            rows={matrixRows}
            brands={brands}
            targetBrand={targetBrand}
            getBrandColor={getBrandColor}
            onDownloadPng={() => handleDownloadPng('#competitive-matrix', 'VisibilityMatrix')}
          />
          <CompetitiveSummary
            targetBrand={targetBrand}
            framing={framing}
            rank={rank}
            comparedAgainst={comparedAgainst}
            getBrandColor={getBrandColor}
          />
          <DrivingQueries queries={drivingQueries} />
          <VerdictBanner
            targetBrand={targetBrand}
            framing={framing}
            verdict={getComparisonVerdict(framing)}
          />
          <CompetitiveCitations targetBrand={targetBrand} comparisons={newestFirst} />
        </div>
      )}
    </div>
  );
};
